import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Apple, Smartphone } from "lucide-react";
import { CallAPIGetPromise, CallAPIPostPromise } from "Common/commaon";


const isEnabled = (value) => value === true || value === 1 || value === "1" || value === "true";

export function EditVersionDialog({
  open,
  onOpenChange,
  app,
  platform,
  onSave,
}) {
  const [formData, setFormData] = useState({
    version: "",
    buildNumber: "",
    status: "development" ,
    forceUpdate: false,
    notes: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (app && platform) {
      setFormData({
        version: platform === "ios" ? app.ios_version : app.android_version,
        buildNumber: platform === "ios" ? app.ios_build_number : app.android_build_number,
        status: platform === "ios" ? app.ios_status : app.android_status,
        forceUpdate: platform === "ios" ? isEnabled(app.ios_force_update) : isEnabled(app.android_force_update),
        notes: "",
      });
    }
  }, [app, platform]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (app && platform) {
      setIsSaving(true);
      try {
        const payload = {
            "id": app.id,
            "name": app.app_name,
            "iosVersion": platform ==="ios"?formData.version:app.ios_version,
            "iosBuildNumber": platform ==="ios"?formData.buildNumber:app.ios_build_number,
            "iosStatus":platform ==="ios"?formData.status:app.ios_status,
            "iosForceUpdate": platform ==="ios"?formData.forceUpdate:isEnabled(app.ios_force_update),
            "androidVersion": platform ==="android"?formData.version:app.android_version,
            "androidBuildNumber": platform ==="android"?formData.buildNumber:app.android_build_number,
            "androidStatus": platform ==="android"?formData.status:app.android_status,
            "androidForceUpdate": platform ==="android"?formData.forceUpdate:isEnabled(app.android_force_update),
            "lastUpdated":app.lastUpdated,
            "createdAt": app.createdAt,
            "updatedAt": app.updatedAt,

        };

        const res = await CallAPIPostPromise(`/admin/updateApp/${app.id}`, payload);
        if (res?.data?.success) {
          onSave(app.id, platform, formData);
          onOpenChange(false);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  if (!app || !platform) return null;

  const platformIcon = platform === "ios" ? <Apple className="w-5 h-5" /> : <Smartphone className="w-5 h-5" />;
  const platformName = platform === "ios" ? "iOS" : "Android";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] tailwind-prefix">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {platformIcon}
            Edit {platformName} Version - {app.app_name}
          </DialogTitle>
          <DialogDescription>
            Update version information and release status for {platformName}.
          </DialogDescription>
        </DialogHeader>

        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="version">Version Number</Label>
                <Input
                  id="version"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="1.0.0"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="buildNumber">Build Number</Label>
                <Input
                  id="buildNumber"
                  value={formData.buildNumber}
                  onChange={(e) => setFormData({ ...formData, buildNumber: e.target.value })}
                  placeholder="1"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Release Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value  })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="development">Development</SelectItem>
                  <SelectItem value="testing">Testing</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="forceUpdate">Force Update</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="forceUpdate"
                  type="checkbox"
                  checked={formData.forceUpdate}
                  disabled={isSaving}
                  onChange={(e) => setFormData((current) => ({
                    ...current,
                    forceUpdate: e.target.checked,
                  }))}
                  className="h-4 w-4"
                />
                <span className="text-sm text-gray-700">
                  Enabled
                </span>
              </div>
              <p className="text-sm text-gray-500">
                Block users on older versions until they update.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Release Notes (optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="What's new in this version..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
